import { useState, useEffect, useCallback } from 'react';
import { getItems, createItem, updateItem, deleteItem } from '../fitness/fitnessApi';

const PROC_LIST = 'Procedures';
const LOG_LIST = 'ProcedureChangeLog';

function safeParse(json) {
  try { return JSON.parse(json) || []; } catch { return []; }
}

export function useProcedures(getToken, userEmail) {
  const [procedures, setProcedures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchAll = useCallback(async () => {
    try {
      setError(null);
      const token = await getToken();
      const items = await getItems(token, PROC_LIST);
      setProcedures(items.map(mapProcedure).sort((a, b) => new Date(b.lastModified || b.createdDate) - new Date(a.lastModified || a.createdDate)));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [getToken]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const createProcedure = useCallback(async (fields) => {
    const token = await getToken();
    const now = new Date().toISOString();
    const created = await createItem(token, PROC_LIST, {
      Title: fields.name,
      Category: fields.category || '',
      Description: fields.description || '',
      Steps: JSON.stringify(fields.steps || []),
      SourceDoc: fields.sourceDoc || '',
      ConversionStatus: fields.conversionStatus || 'not_started',
      ConversionProgress: fields.conversionProgress || 0,
      CreatedBy: userEmail,
      CreatedDate: now,
      LastModified: now,
      RoleTag: fields.roleTag || '',
    });
    await logChange(token, created.id, fields.name, 'procedure_created', 0, '', fields.name);
    await fetchAll();
    return created;
  }, [getToken, userEmail, fetchAll]);

  const updateProcedure = useCallback(async (id, fields) => {
    const token = await getToken();
    const updates = { ...fields, LastModified: new Date().toISOString() };
    if (fields.steps) updates.Steps = JSON.stringify(fields.steps);
    if (fields.name) updates.Title = fields.name;
    await updateItem(token, PROC_LIST, id, updates);
    await fetchAll();
  }, [getToken, fetchAll]);

  const deleteProcedure = useCallback(async (id) => {
    const token = await getToken();
    await deleteItem(token, PROC_LIST, id);
    setProcedures(prev => prev.filter(p => p.id !== id));
  }, [getToken]);

  // Step operations — all auto-log changes
  const addStep = useCallback(async (procId, step, afterIndex = null) => {
    const proc = procedures.find(p => p.id === procId);
    if (!proc) return;
    const token = await getToken();
    const steps = [...proc.steps];
    const now = new Date().toISOString();
    const newStep = {
      stepNumber: 0, // will renumber
      title: step.title || '',
      details: step.details || '',
      notes: step.notes || '',
      lastModified: now,
      modifiedBy: userEmail,
    };
    if (afterIndex !== null && afterIndex >= 0) {
      steps.splice(afterIndex + 1, 0, newStep);
    } else {
      steps.push(newStep);
    }
    renumber(steps);
    await updateItem(token, PROC_LIST, procId, { Steps: JSON.stringify(steps), LastModified: now });
    await logChange(token, procId, proc.name, 'step_added', newStep.stepNumber, '', newStep.title);
    await fetchAll();
  }, [getToken, userEmail, procedures, fetchAll]);

  const editStep = useCallback(async (procId, stepIndex, updates) => {
    const proc = procedures.find(p => p.id === procId);
    if (!proc) return;
    const token = await getToken();
    const steps = [...proc.steps];
    const oldStep = { ...steps[stepIndex] };
    const now = new Date().toISOString();
    steps[stepIndex] = { ...steps[stepIndex], ...updates, lastModified: now, modifiedBy: userEmail };
    await updateItem(token, PROC_LIST, procId, { Steps: JSON.stringify(steps), LastModified: now });
    await logChange(token, procId, proc.name, 'step_edited', stepIndex + 1, oldStep.title + ': ' + oldStep.details, steps[stepIndex].title + ': ' + steps[stepIndex].details);
    await fetchAll();
  }, [getToken, userEmail, procedures, fetchAll]);

  const deleteStep = useCallback(async (procId, stepIndex) => {
    const proc = procedures.find(p => p.id === procId);
    if (!proc) return;
    const token = await getToken();
    const steps = [...proc.steps];
    const removed = steps.splice(stepIndex, 1)[0];
    renumber(steps);
    const now = new Date().toISOString();
    await updateItem(token, PROC_LIST, procId, { Steps: JSON.stringify(steps), LastModified: now });
    await logChange(token, procId, proc.name, 'step_deleted', stepIndex + 1, removed.title, '');
    await fetchAll();
  }, [getToken, procedures, fetchAll]);

  const reorderStep = useCallback(async (procId, fromIndex, toIndex) => {
    const proc = procedures.find(p => p.id === procId);
    if (!proc) return;
    const token = await getToken();
    const steps = [...proc.steps];
    const [moved] = steps.splice(fromIndex, 1);
    steps.splice(toIndex, 0, moved);
    renumber(steps);
    const now = new Date().toISOString();
    await updateItem(token, PROC_LIST, procId, { Steps: JSON.stringify(steps), LastModified: now });
    await logChange(token, procId, proc.name, 'step_reordered', fromIndex + 1, `Step ${fromIndex + 1}`, `Step ${toIndex + 1}`);
    await fetchAll();
  }, [getToken, procedures, fetchAll]);

  // Batch save steps (for conversion)
  const saveSteps = useCallback(async (procId, steps, extraFields = {}) => {
    const token = await getToken();
    renumber(steps);
    const now = new Date().toISOString();
    await updateItem(token, PROC_LIST, procId, {
      Steps: JSON.stringify(steps),
      LastModified: now,
      ...extraFields,
    });
    await fetchAll();
  }, [getToken, fetchAll]);

  const getChangeLog = useCallback(async (procId) => {
    const token = await getToken();
    const items = await getItems(token, LOG_LIST);
    return items
      .filter(it => it.ProcedureId === procId)
      .map(it => ({
        id: it.id,
        changeType: it.ChangeType || '',
        stepNumber: it.StepNumber || 0,
        oldValue: it.OldValue || '',
        newValue: it.NewValue || '',
        changedBy: it.ChangedBy || '',
        changedDate: it.ChangedDate || '',
        procedureName: it.ProcedureName || '',
      }))
      .sort((a, b) => new Date(b.changedDate) - new Date(a.changedDate));
  }, [getToken]);

  async function logChange(token, procId, procName, changeType, stepNumber, oldValue, newValue) {
    try {
      await createItem(token, LOG_LIST, {
        Title: `${procName} — ${changeType}`,
        ProcedureId: procId,
        ProcedureName: procName,
        ChangeType: changeType,
        StepNumber: stepNumber,
        OldValue: oldValue,
        NewValue: newValue,
        ChangedBy: userEmail,
        ChangedDate: new Date().toISOString(),
      });
    } catch { /* logging should never block */ }
  }

  return {
    procedures, loading, error,
    createProcedure, updateProcedure, deleteProcedure,
    addStep, editStep, deleteStep, reorderStep, saveSteps,
    getChangeLog, refetch: fetchAll,
  };
}

function mapProcedure(it) {
  return {
    id: it.id,
    name: it.Title || '',
    category: it.Category || '',
    description: it.Description || '',
    steps: safeParse(it.Steps),
    sourceDoc: it.SourceDoc || '',
    conversionStatus: it.ConversionStatus || 'not_started',
    conversionProgress: it.ConversionProgress || 0,
    createdBy: it.CreatedBy || '',
    createdDate: it.CreatedDate || '',
    lastModified: it.LastModified || '',
    roleTag: it.RoleTag || '',
  };
}

function renumber(steps) {
  steps.forEach((s, i) => { s.stepNumber = i + 1; });
}
